/*
This file is located in the main branch
Last time the file was updated (UTC): [11/7/2024 5:40:40AM]
Permissions to update outside the main branch: [false]
*/

import {
    ColumnType,
    Filter,
    getFilterElementsDefault,
    Setting,
    SettingAdd,
    SettingChange,
    SettingTable,
    useWorkplaceState,
} from '@finbeat/react-toolkit';
import { faBoltLightning } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    addSettingsItem,
    changeSettingsItem,
    deleteSettingItem,
    getSettingItem,
    getSettings,
    getSettingsExcel,
} from 'axios/settingsApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { map } from 'mappers/mapper';
import { settingsToWorkplace } from 'mappers/settings';
import DataGridProp from 'models/dataGridProp';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useCallback, useEffect, useState } from 'react';
import { FormatterProps } from 'react-data-grid';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { Spinner } from 'reactstrap';
import { deleteActionBtnFormatter } from 'utils/columnFormatters';
import SettingsAddButton from './SettingsAddButton';
import SettingsChangeModal from './SettingsChangeModal';
import SettingsFilter from './SettingsFilter';
import DictItem from 'models/api/dictItem';

type SettingsFormatterProps = FormatterProps<SettingTable, SettingTable[]>;

const Settings = () => {
    const { t, i18n } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const { cultures } = useGeneralSettingsContext();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [columns, setColumns] = useState<
        ColumnType<DataGridProp & SettingTable, SettingTable[]>[]
    >([]);

    const { hasRole } = useAuth();
    const getItemRole = 'websettingsGetByCode';
    const createRole = 'websettingsCreate';
    const deleteRole = 'websettingsDelete';

    const codeFormatter = useCallback(
        ({ row: { code, translations } }: SettingsFormatterProps) => (
            <div>
                {code}
                <p className="text-secondary">
                    {translations.find((t) => t.cultureCode === i18n.language)?.dsc}
                </p>
            </div>
        ),
        [t, i18n.language]
    );

    const culturesFormatter = useCallback((i: DictItem) => {
        const component = ({ row: { translations } }: SettingsFormatterProps) => (
            <>{translations.find((y) => y.cultureCode === i.code)?.value}</>
        );
        return component;
    }, []);

    useEffect(() => {
        if (!cultures) return;
        setColumns([
            {
                key: 'code',
                name: <Trans i18nKey={'BackOfficeCode'} />,
                formatter: codeFormatter,
            },
            ...cultures.map((x) => ({
                key: x.code,
                name: x.name,
                formatter: culturesFormatter(x),
            })),
            {
                key: 'actions',
                name: <FontAwesomeIcon icon={faBoltLightning} />,
                cellClass: 'cell-centered no-change-modal',
                headerCellClass: 'cell-centered',
                width: 70,
                minWidth: 70,
                resizable: false,
                clickDisable: true,
                formatter: deleteActionBtnFormatter(hasRole(deleteRole)),
            },
        ]);
    }, [cultures]);

    return !columns ? (
        <Spinner />
    ) : (
        <BOWorkplace<SettingTable, SettingTable, Filter<Setting>, SettingChange, SettingAdd>
            testId="settings"
            pageTitle="BackOfficeSettings"
            filterParams={{
                code: '',
                value: '',
                dsc: '',
            }}
            initColumns={columns}
            storagePath="settings/columns"
            getFilterElements={getFilterElementsDefault([
                { name: t('BackOfficeCode'), id: 'code' },
                { name: t('BackOfficeValue'), id: 'value' },
                { name: t('BackOfficeDescription'), id: 'dsc' },
            ])}
            showModal={hasRole(getItemRole) ? () => setIsModalVisible(true) : undefined}
            filter={<SettingsFilter />}
            modal={{
                component: (
                    <SettingsChangeModal isOpen={isModalVisible} toggle={setIsModalVisible} />
                ),
                addMutationFn: (item) => addSettingsItem(api, item),
                changeMutationFn: (item) => changeSettingsItem(api, item),
                fetchCurrentItemQueryFn: (id, item) => getSettingItem(api, item),
            }}
            fetchQueryFn={(filter) => map(getSettings(api, filter), settingsToWorkplace)}
            buttons={
                hasRole(createRole)
                    ? [<SettingsAddButton key="1" showModal={() => setIsModalVisible(true)} />]
                    : []
            }
            queryKey={['webSettings']}
            queryClient={queryClient}
            fetchExcelQueryFn={(filter) => getSettingsExcel(api, filter)}
            deleteMutationFn={(item) => deleteSettingItem(api, item as string)}
        />
    );
};

const useSettingsWorkplaceState = () =>
    useWorkplaceState<SettingTable, SettingTable, SettingChange, SettingAdd>();

export default Settings;
export { useSettingsWorkplaceState };
